import * as React from 'react';
import { Box, Button, LinearProgress, MenuItem, Typography } from '@material-ui/core';
import { CheckCircleOutlineOutlined, SvgIconComponent } from '@material-ui/icons';
import { createStyles, makeStyles, Theme } from '@material-ui/core/styles';
import { DropdownMenu } from '../DropdownMenu';
import { localizeNumber } from '@pec/aion-ui-i18next/helpers/localize';
import { OrganizationFeature } from '@pec/aion-ui-core/interfaces/organization';
import { QuestionStatusIcon } from '@pec/aion-ui-components/components/QuestionStatusIcon';
import { SubscriptionTypePublicId } from '@pec/aion-ui-core/enums/Subscription';
import { TFunction } from 'i18next';
import { useParams } from 'react-router-dom';
import { usePermission } from '../../hooks/usePermission';
import { useQuestionnaireProgress } from 'hooks/useQuestionnaireProgress';
import { useTranslation } from 'react-i18next';
import { useTypedSelector } from 'combineReducers';
import { Widget } from '../Widget';
import { QuestionnaireCHAS } from './QuestionnaireCHAS';
import moment from 'moment';
import { OperationType } from '../../types/enums';
import ReportProblemOutlinedIcon from '@material-ui/icons/ReportProblemOutlined';
import { Message } from '@pec/aion-ui-components/components/Message';
import { useScheduledRender } from '@pec/aion-ui-components/hooks/useScheduledRender';
import { VERIFICATION_ORGANIZATION_ID } from '@pec/aion-ui-core/constants/organizations';
import { ActivityAction } from '@pec/aion-ui-core/interfaces/activities';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    container: {
      margin: theme.spacing(2, 0, 0)
    },
    progressValue: {
      fontWeight: theme.typography.fontWeightMedium,
      lineHeight: '24px',
      textAlign: 'center'
    },
    progressBar: {
      height: 4,
      marginBottom: theme.spacing(2)
    },
    progressColor: {
      backgroundColor: 'var(--progressbar-background)'
    },
    progressBarColor: {
      borderRadius: 4
    },
    menuTitle: {
      display: 'flex',
      gap: `${theme.spacing(2)}px`,
      padding: `${theme.spacing(1.5)}px ${theme.spacing(0.5)}px`
    },
    menuTitleIcon: {
      color: 'var(--light-outline)'
    },
    viewAllBtn: {
      marginTop: theme.spacing(2)
    },
    warningIcon: {
      color: '#EF6C00 !important',
      marginRight: theme.spacing(1)
    },
    text: {
      fontSize: '1rem',
      lineHeight: '143%',
      letterSpacing: '0.17',
      color: 'var(--status-warning-text)'
    }
  })
);

const getStatusCategories = (
  t: TFunction
): {
  Icon: SvgIconComponent | React.FC;
  label: string;
  dataKey: 'notAnswered' | 'inProgress' | 'answered';
}[] => [
  {
    label: t('newDashboard.questionnaire.notAnswered', 'Not answered'),
    Icon: () => <QuestionStatusIcon status="notStarted" />,
    dataKey: 'notAnswered'
  },
  {
    label: t('newDashboard.questionnaire.inProgress', 'In progress'),
    Icon: () => <QuestionStatusIcon status="inProgress" />,
    dataKey: 'inProgress'
  },
  {
    label: t('newDashboard.questionnaire.answered', 'Answered'),
    Icon: () => <CheckCircleOutlineOutlined style={{ color: '#00953B' }} />,
    dataKey: 'answered'
  }
];

const { ESGContractor, UnlockModulesBasedOnPreviousDocExpiryDate } = OrganizationFeature;

export const Questionnaire: React.FC = () => {
  const classes = useStyles();
  const { t } = useTranslation('subscriber');
  const { organizationId } = useParams<{
    organizationId: string;
  }>();

  const { hasContractorQuestionnairePermission } = usePermission();
  const { hasContractorQuestionnairePermission: hasContractorQuestionnaireManagePermission } = usePermission(
    ActivityAction.Manage
  );
  const { organization } = useTypedSelector(state => state.organization);
  const { organizationInfo } = useTypedSelector(state => state.organizationInfo);
  const { allUserOrganizations } = useTypedSelector(state => state.userOrganizations);

  const {
    isFetching,
    error,
    hideProgressBar,
    progress,
    answersToBePublishedCount,
    mainSubscription,
    statusSegmentSections,
    publishedProgress
  } = useQuestionnaireProgress();

  const { createdDate, typePublicId, countryISO2, operationType } = mainSubscription;
  const hasFoundationSubscription = typePublicId === SubscriptionTypePublicId.ChasFoundation;
  const hasESGFeature = organization && organization?.features.includes(ESGContractor);
  const unlockNewModuleBasedOnPreviousDocumentExpiryDate =
    organization && organization?.features.includes(UnlockModulesBasedOnPreviousDocExpiryDate);
  const isFoundationESGEligible = hasFoundationSubscription && hasESGFeature;
  const verificationStatusesCalculationInProgress =
    createdDate &&
    moment
      .utc()
      .isBefore(moment.utc(createdDate).add(process.env.REACT_APP_VERIFICATION_STATUSES_CALC_TIMEOUT, 'seconds'));
  const modulesAreHidden = Boolean(
    unlockNewModuleBasedOnPreviousDocumentExpiryDate &&
      verificationStatusesCalculationInProgress &&
      operationType === OperationType.Renewal
  );
  const subsidiaryBlocked =
    organizationInfo?.isChild &&
    !(
      allUserOrganizations?.some(organization => organization.id === VERIFICATION_ORGANIZATION_ID) &&
      hasContractorQuestionnaireManagePermission
    );
  useScheduledRender(modulesAreHidden);

  if (!hasContractorQuestionnairePermission) {
    return null;
  }

  return (
    <Widget
      title={t('newDashboard.questionnaire.title', 'Questionnaire')}
      isFetching={isFetching}
      hasFetchingError={!!error}
    >
      {modulesAreHidden || subsidiaryBlocked ? (
        <Message variant="warning" CustomIcon={<ReportProblemOutlinedIcon className={classes.warningIcon} />}>
          <Typography className={classes.text}>
            {modulesAreHidden
              ? t(
                  'newDashboard.questionnaire.notAvailableMessage',
                  'Please wait whilst your questionnaire is being prepared. This is obtaining the latest version of the questionnaire that supports regulatory changes recently made. This will take around a minute.'
                )
              : t(
                  'newDashboard.questionnaire.subsidiaryBlockedMessage',
                  'This organisation is a subsidiary of a parent organisation and is part of a group assessment. Please navigate to the parent organisation to review the questionnaire.'
                )}
          </Typography>
        </Message>
      ) : (
        <Box className={classes.container}>
          {!hideProgressBar && (
            <>
              <LinearProgress
                variant="determinate"
                value={publishedProgress}
                classes={{
                  root: classes.progressBar,
                  colorPrimary: classes.progressColor,
                  barColorPrimary: classes.progressBarColor
                }}
              />
              <Typography classes={{ root: classes.progressValue }} variant="h6">
                {localizeNumber(
                  {
                    value: publishedProgress / 100,
                    config: {
                      style: 'percent'
                    }
                  },
                  t
                )}{' '}
                {t('newDashboard.questionnaire.completed', 'Completed')}
              </Typography>
              {answersToBePublishedCount > 0 && (
                <Typography align="center" variant="body2">
                  {t(
                    'newDashboard.questionnaire.answersPendingPublication',
                    '{{pendingQuestionsCount}} answers pending publication',
                    {
                      pendingQuestionsCount: answersToBePublishedCount
                    }
                  )}
                </Typography>
              )}
            </>
          )}
          <Box px={0} pt={hideProgressBar ? 0 : 3}>
            {countryISO2 === 'US' ? (
              <>
                {getStatusCategories(t).map(({ Icon, label, dataKey }) => {
                  return progress[dataKey].length > 0 ? (
                    <DropdownMenu
                      key={dataKey}
                      title={
                        <Box className={classes.menuTitle}>
                          <Icon className={classes.menuTitleIcon} />
                          <Typography variant="body1">
                            {label} ({progress[dataKey].length})
                          </Typography>
                        </Box>
                      }
                    >
                      {progress[dataKey].map(sectionStatus => (
                        <MenuItem
                          key={sectionStatus.sectionId}
                          component="a"
                          href={`/organizations/${organizationId}/questionnaire-v2/${sectionStatus.sectionId}`}
                        >
                          {sectionStatus.sectionTitle}
                        </MenuItem>
                      ))}
                    </DropdownMenu>
                  ) : null;
                })}
              </>
            ) : null}
            {countryISO2 !== 'US' && !isFoundationESGEligible ? (
              <QuestionnaireCHAS statusSegmentSections={statusSegmentSections} />
            ) : null}
          </Box>
          <Button
            variant="contained"
            color="primary"
            className={classes.viewAllBtn}
            component="a"
            href={`/organizations/${organizationId}/questionnaire-v2`}
          >
            {isFoundationESGEligible
              ? t('newDashboard.questionnaire.view', 'View')
              : t('newDashboard.questionnaire.viewAll', 'View All')}
          </Button>
        </Box>
      )}
    </Widget>
  );
};
